import { useEffect, useRef, useState } from "react";
import { Check, Plus, Star } from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { t } from "i18next";
import axios from "axios";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { errorMessage } from "../../global-components/src/utils/text";
import { compressImage, convertHeic } from "../../global-components/src/utils/file";
import MediaEditor from "../media-editor";
import { useMediaEditor } from "../../hooks/useMediaEditor";
import { Story } from "../../types";
import BestFriendsPicker from "./best-friends-picker";
import { shouldOpenBestFriendsPicker, shouldPublishToBestFriends } from "./best-friends";
import { formatUploadBytes, type ResumableVideoUploadProgress, uploadResumableVideo } from "../../utils/resumable-video-upload";

const MIN_DIMENSION = 150;

// ─── Componente ───────────────────────────────────────────────────────────────
interface AddStoryProps {
  onStoryAdded: (story: Story) => void;
}

const AddStory = ({ onStoryAdded }: AddStoryProps) => {
  const { token, user }      = useAuth();
  const { setMessage } = useMessage();
  const inputRef       = useRef<HTMLInputElement | null>(null);

  const [bestFriendsOnly, setBestFriendsOnly] = useState(false);
  const [bestFriendsCount, setBestFriendsCount] = useState(0);
  const [showBestFriendsPicker, setShowBestFriendsPicker] = useState(false);
  const bestFriendsHoldTimer = useRef<number | null>(null);
  const didOpenBestFriendsPicker = useRef(false);
  const [videoUpload, setVideoUpload] = useState<ResumableVideoUploadProgress | null>(null);
  const videoUploadAbortRef = useRef<AbortController | null>(null);

  // Hook que controla abertura/fechamento do MediaEditor
  const editor = useMediaEditor();

  useEffect(() => {
    if (!token) return;

    axios.get(`${PACOCA_API_URL}/users/best-friends`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      setBestFriendsCount((data.best_friends ?? []).length);
    }).catch(() => {
      // A seleção continuará disponível quando o usuário tocar no botão.
    });
  }, [token]);

  const openBestFriendsPicker = () => setShowBestFriendsPicker(true);

  const toggleBestFriendsOnly = () => {
    if (shouldOpenBestFriendsPicker(bestFriendsCount)) {
      openBestFriendsPicker();
      return;
    }

    setBestFriendsOnly(previous => !previous);
  };

  const handleBestFriendsSaved = (bestFriends: { id: number }[]) => {
    setBestFriendsCount(bestFriends.length);
    setBestFriendsOnly(bestFriends.length > 0);
  };

  const clearBestFriendsHold = () => {
    if (bestFriendsHoldTimer.current !== null) {
      window.clearTimeout(bestFriendsHoldTimer.current);
      bestFriendsHoldTimer.current = null;
    }
  };

  const startBestFriendsHold = () => {
    didOpenBestFriendsPicker.current = false;
    clearBestFriendsHold();
    bestFriendsHoldTimer.current = window.setTimeout(() => {
      didOpenBestFriendsPicker.current = true;
      openBestFriendsPicker();
    }, 500);
  };

  // ─── Upload ────────────────────────────────────────────────────────────────

  /**
   * Recebe o File já processado (crop + filtro + trim aplicados pelo MediaEditor)
   * e só faz o upload para a API.
   */
  const handleConfirm = async (file: File) => {
    try {
      const type = file.type.startsWith("image") ? "image" : "video";

      let path: string;
      if (type === 'video') {
        const controller = new AbortController();
        videoUploadAbortRef.current = controller;
        const uploaded = await uploadResumableVideo({
          api: axios,
          apiBaseUrl: PACOCA_API_URL,
          headers: { Authorization: `Bearer ${token}` },
          file,
          signal: controller.signal,
          onProgress: setVideoUpload,
        });
        path = uploaded.path;
      } else {
        const { data: uploadUrls } = await axios.post(
          `${PACOCA_API_URL}/generate-upload-urls`,
          { files: [{ type, name: file.name }] },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await axios.put(uploadUrls[0].url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
        path = uploadUrls[0].path;
      }

      // Registra o story na API depois da confirmação do GCS.
      const { data } = await axios.post(
        `${PACOCA_API_URL}/stories`,
        {
          image_path: path,
          best_friends_only: shouldPublishToBestFriends(bestFriendsOnly, bestFriendsCount),
        },
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } },
      );

      const story: Story = {
        ...data.story,
        user: data.story.user ?? user,
      };
      onStoryAdded(story);
      setMessage(data.message, "success");
      // O hook fecha o editor automaticamente após onConfirm resolver
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? errorMessage(err);
      setMessage(msg, "error");
      throw err;
    } finally {
      videoUploadAbortRef.current = null;
      setVideoUpload(null);
    }
  };

  // ─── Seleção de arquivo ────────────────────────────────────────────────────

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0];
    if (!file) return;

    // Limpa o input para permitir re-selecionar o mesmo arquivo depois
    e.target.value = "";

    if (file.type.startsWith("image/")) {
      // Pré-processamento: converte HEIC e comprime antes de abrir o editor
      file = await convertHeic(file);
      file = await compressImage(file);

      // Valida dimensão mínima
      const url = await fileToDataURL(file);
      const valid = await checkImageDimension(url, MIN_DIMENSION);
      if (!valid) {
        setMessage("A imagem precisa ter pelo menos 150 × 150 px.", "error");
        return;
      }
    }

    // Abre o editor passando o arquivo + callbacks
    editor.open(file, {
      onConfirm: handleConfirm,
      onCancel: () => {
        // nada adicional necessário; o hook já fecha o editor
      },
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Botão na barra de stories */}
      <div className="story-item" onClick={() => inputRef.current?.click()}>
        <div className="story-avatar as-add-avatar">
          <Plus size={24} />
        </div>
        <span>{t("generics.new")}</span>
      </div>

      {videoUpload ? <div className="story-upload-progress" role="status">{videoUpload.state === 'retrying' ? 'Reconectando o vídeo…' : 'Enviando vídeo…'} {Math.round((videoUpload.confirmedBytes / Math.max(videoUpload.totalBytes, 1)) * 100)}% · {formatUploadBytes(videoUpload.confirmedBytes)} / {formatUploadBytes(videoUpload.totalBytes)} <button type="button" onClick={() => videoUploadAbortRef.current?.abort()}>Cancelar</button></div> : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        className="d-none"
        onChange={onSelectFile}
      />

      {/*
        MediaEditor recebe tudo via editor.props (file, onConfirm, onCancel).
        A prop `footer` injeta o seletor de privacidade no rodapé do editor,
        mantendo o design original sem precisar modificar o MediaEditor.
      */}

      {editor.isOpen && (
        <MediaEditor
          {...editor.props}
          footer={(
            <button
              type="button"
              className={`btn btn-sm d-flex align-items-center gap-2 ${bestFriendsOnly ? "btn-primary" : "btn-outline-secondary"}`}
              onPointerDown={startBestFriendsHold}
              onPointerUp={clearBestFriendsHold}
              onPointerLeave={clearBestFriendsHold}
              onClick={() => {
                if (didOpenBestFriendsPicker.current) {
                  didOpenBestFriendsPicker.current = false;
                  return;
                }
                toggleBestFriendsOnly();
              }}
              onContextMenu={event => {
                event.preventDefault();
                clearBestFriendsHold();
                openBestFriendsPicker();
              }}
              title="Toque para ativar. Segure para editar os melhores amigos."
            >
              {bestFriendsOnly ? <Check size={16} /> : <Star size={16} />}
              Melhores amigos
            </button>
          )}
        />
      )}

      <BestFriendsPicker
        show={showBestFriendsPicker}
        onClose={() => setShowBestFriendsPicker(false)}
        onSaved={handleBestFriendsSaved}
      />
    </>
  );
};

export default AddStory;

// ─── Utilitários locais ───────────────────────────────────────────────────────

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function checkImageDimension(dataUrl: string, minPx: number): Promise<boolean> {
  return new Promise((resolve) => {
    const img  = new Image();
    img.onload = () => resolve(img.naturalWidth >= minPx && img.naturalHeight >= minPx);
    img.onerror = () => resolve(false);
    img.src    = dataUrl;
  });
}